import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowUpTrayIcon, 
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';


export default function BulkUpload() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);
  const navigate = useNavigate();
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type (CSV only)
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      setSelectedFile(null);
      return;
    }
    
    setSelectedFile(file);
    setError(null);
  };
  
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }
    
    setIsUploading(true);
    setError(null);
    
    try {
      // In a real app, this would be an API call
      // const result = await bulkUploadProducts(selectedFile);
      
      // Mock result for demonstration
      const result = {
        success: 15,
        failed: 2,
        errors: [
          'Row 3: Invalid price format',
          'Row 7: Missing required field "name"'
        ]
      };
      
      setUploadResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to upload products');
    } finally {
      setIsUploading(false);
    }
  };
  
  const downloadTemplate = () => {
    // Create CSV template content
    const templateContent = 'name,description,original_price,discount_price,expiry_date,start_time,end_time,quantity,nutrition_info,location\n' +
      'Organic Apples,"Fresh organic apples, 5lb bag",8.99,4.99,2025-05-15,08:00,17:00,10,"{\"calories\":\"52\",\"protein\":\"0.3g\",\"fat\":\"0.2g\",\"carbs\":\"14g\"}","123 Main Street, Hamilton, Bermuda"\n' +
      'Whole Grain Bread,"Freshly baked whole grain bread",5.49,2.99,2025-05-10,09:00,18:00,8,"{\"calories\":\"80\",\"protein\":\"4g\",\"fat\":\"1g\",\"carbs\":\"15g\"}","45 Front Street, Hamilton, Bermuda"\n';
    
    // Create a blob and download link
    const blob = new Blob([templateContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  return (
      <div className="space-y-6">
        <div className="flex items-center">
          <h1 className="text-xl font-medium text-navy-blue">Bulk Upload Products</h1>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-navy-blue">Upload CSV File</h2>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              <div>
                <p className="text-sm text-gray-500">
                  Upload a CSV file containing product information. Each row should represent a product with the following columns:
                </p>
                <ul className="mt-2 list-disc pl-5 text-sm text-gray-500">
                  <li>name (required): Product name</li>
                  <li>location: Physical location where the product is available</li>
                  <li>description: Product description</li>
                  <li>original_price (required): Original price in dollars</li>
                  <li>discount_price (required): Discounted price in dollars</li>
                  <li>expiry_date (required): Expiry date in YYYY-MM-DD format</li>
                  <li>start_time: Available from time in HH:MM format (24-hour)</li>
                  <li>end_time: Available until time in HH:MM format (24-hour)</li>
                  <li>quantity (required): Available quantity</li>
                  <li>nutrition_info: JSON string with nutrition information</li>
                </ul>
              </div>
              
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
                >
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-gray-500" />
                  Download Template
                </button>
              </div>
              
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  {selectedFile ? (
                    <div>
                      <DocumentTextIcon className="mx-auto h-12 w-12 text-sky-blue" />
                      <p className="text-sm text-gray-600 mt-2">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </p>
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => setSelectedFile(null)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
                        >
                          Change file
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-sky-blue hover:text-teal focus-within:outline-none"
                        >
                          <span>Upload a file</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            accept=".csv"
                            onChange={handleFileChange}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        CSV files only, up to 10MB
                      </p>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-navy-blue hover:bg-sky-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                      Upload Products
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {uploadResult && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-navy-blue">Upload Results</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 rounded-md p-4 flex items-center">
                  <CheckCircleIcon className="h-8 w-8 text-green-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      {uploadResult.success} products uploaded successfully
                    </p>
                  </div>
                </div>
                
                {uploadResult.failed > 0 && (
                  <div className="bg-red-50 rounded-md p-4 flex items-center">
                    <XCircleIcon className="h-8 w-8 text-red-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        {uploadResult.failed} products failed to upload
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {uploadResult.errors.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Errors:</h3>
                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                    {uploadResult.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/products')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-navy-blue hover:bg-sky-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
                >
                  Go to Products
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}
